import { PCM16_BYTES_PER_SAMPLE } from '../constants/coSellerMic';

const INT16_NEGATIVE_SCALE = 0x8000;
const INT16_POSITIVE_SCALE = 0x7fff;

/** Float samples in [-1, 1] as 16-bit little-endian PCM (`linear16`), clamped. */
export const toPcm16le = (samples: Float32Array): ArrayBuffer => {
  const buffer = new ArrayBuffer(samples.length * PCM16_BYTES_PER_SAMPLE);
  const view = new DataView(buffer);
  samples.forEach((sample, index) => {
    const clamped = Math.max(-1, Math.min(1, sample));
    const scaled = clamped < 0 ? clamped * INT16_NEGATIVE_SCALE : clamped * INT16_POSITIVE_SCALE;
    view.setInt16(index * PCM16_BYTES_PER_SAMPLE, Math.round(scaled), true);
  });
  return buffer;
};

/**
 * Streaming linear-interpolation resampler, block by block, with no seam
 * between blocks. Chrome and Safari honour a 16 kHz AudioContext and hand it
 * 16 kHz already (this is then a pass-through); a browser that cannot connect
 * the mic to a 16 kHz context runs at its native rate and lands here. Linear
 * interpolation has no anti-alias filter: fine for speech recognition and the
 * proxy's loudness envelope, which is all the stream is used for.
 */
export class LinearResampler {
  /** Input samples per output sample. */
  private readonly step: number;
  /** Where the next output sample falls, in input samples from the next block's start (>= -1). */
  private nextPosition = 0;
  /** The previous block's last sample: index -1 of the next block. */
  private previousSample = 0;

  constructor(inputRate: number, outputRate: number) {
    if (!(inputRate > 0) || !(outputRate > 0)) {
      throw new Error(`Resampler rates must be positive, got ${inputRate} -> ${outputRate}`);
    }
    this.step = inputRate / outputRate;
  }

  process(input: Float32Array): Float32Array {
    if (this.step === 1) return input;
    const sampleAt = (index: number): number => (index < 0 ? this.previousSample : input[index]);
    const output: number[] = [];
    let position = this.nextPosition;
    while (position <= input.length - 1) {
      const index = Math.floor(position);
      const fraction = position - index;
      const before = sampleAt(index);
      const after = fraction === 0 ? before : sampleAt(index + 1);
      output.push(before + (after - before) * fraction);
      position += this.step;
    }
    this.nextPosition = position - input.length;
    if (input.length > 0) this.previousSample = input[input.length - 1];
    return Float32Array.from(output);
  }
}

export interface PcmFrame {
  /** `linear16` bytes, exactly one frame long. */
  bytes: ArrayBuffer;
  /** Index of the frame's first sample in the whole captured stream (0-based). */
  startSample: number;
}

/** Cuts a continuous sample stream into fixed-size `linear16` frames. */
export class PcmFrameAccumulator {
  private readonly pending: Float32Array;
  private pendingCount = 0;
  private acceptedSamples = 0;

  constructor(private readonly frameSamples: number) {
    if (!Number.isInteger(frameSamples) || frameSamples <= 0) {
      throw new Error(`Frame size must be a positive whole number of samples, got ${frameSamples}`);
    }
    this.pending = new Float32Array(frameSamples);
  }

  /** Samples accepted so far: the stream position of the newest one, plus one. */
  get totalSamples(): number {
    return this.acceptedSamples;
  }

  /** Every frame `samples` completes, oldest first. A partial frame waits for the next push. */
  push(samples: Float32Array): PcmFrame[] {
    const frames: PcmFrame[] = [];
    let offset = 0;
    while (offset < samples.length) {
      const taken = Math.min(this.frameSamples - this.pendingCount, samples.length - offset);
      this.pending.set(samples.subarray(offset, offset + taken), this.pendingCount);
      this.pendingCount += taken;
      offset += taken;
      if (this.pendingCount === this.frameSamples) {
        const startSample = this.acceptedSamples + offset - this.frameSamples;
        frames.push({ bytes: toPcm16le(this.pending), startSample });
        this.pendingCount = 0;
      }
    }
    this.acceptedSamples += samples.length;
    return frames;
  }
}
